import { Field } from 'formik'
import PropTypes from 'prop-types'
import React from 'react'
import { PaymentElements } from '@client/components/PaymentElements'
import { Button } from 'components/Button'
import { Callout, CALLOUT_INTENT } from 'components/Callout'
import CardSelectGroup from 'components/CardSelect'
import { CheckboxItem } from 'components/Checkbox/CheckboxItem'
import Icon from 'components/Icon'
import { Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { MediaQueryWrapper } from 'components/MediaQueryWrapper'
import { Textarea } from 'components/Textarea'
import { H2, H4, Text } from 'components/Typography'
import { SPACE, COLOR, FONT_WEIGHT } from 'Theme'
import { triggerValidChange } from 'utils/formikValidChange'
import { couponCheckoutValidationSchema } from './validationSchema'

const SIGNED_IN_LABEL =
  'You’ll receive the gift card to this email address immediately upon purchase'
const GUEST_LABEL =
  'Enter your email address. We will send you the gift card code that you can forward or print out for the recipient.'
const COUPON_VALUES = [300, 500, 800]

export const CouponCheckoutElements = ({
  isPurchaseSubmitting,
  isLoading,
  onChangeAmount,
  values = {},
  setFieldValue,
  isClientSignedIn,
  availablePaymentOptions,
  handleSubmit,
  isLoadingOptions,
}) => (
  <Flex
    flexDirection="column"
    mr={{ MOBILE: 0, TABLET: SPACE.PX_30, DESKTOP: SPACE.PX_120 }}
    width="100%"
  >
    <H2 mb={SPACE.PX_30}>Your order</H2>
    <div>
      <Field name="contactEmail">
        {({ field, meta }) => (
          <Box mb={SPACE.PX_20}>
            <H4>Email Address</H4>
            <Input
              label={isClientSignedIn ? SIGNED_IN_LABEL : GUEST_LABEL}
              isDisabled={
                isLoading ||
                isPurchaseSubmitting ||
                (isClientSignedIn && field.value)
              }
              hasHint
              isError={meta.touched && meta.error !== undefined}
              hint={meta.touched ? meta.error : ''}
              {...field}
            />
            <Field name="isDifferentRecipient" type="checkbox">
              {({ field: differentRecipientCheckbox }) => (
                <CheckboxItem
                  isChecked={differentRecipientCheckbox.value}
                  {...differentRecipientCheckbox}
                >
                  I am buying gift card for someone else
                </CheckboxItem>
              )}
            </Field>
          </Box>
        )}
      </Field>
      {values.isDifferentRecipient && (
        <Box mb={SPACE.PX_20}>
          <H4>Gift card recipient</H4>
          <Callout
            intent={CALLOUT_INTENT.INFO}
            icon={Icon.Info}
            marginBottom={SPACE.PX_30}
          >
            Please note, gift cards will be sent immediately after the purchase.
            If you wish to gift this at a later date, leave this section blank
            and the gift card will be delivered to your email.
          </Callout>
          <Field name="recipientFirstName">
            {({ field, meta }) => (
              <Input
                label="Recipient's first name"
                isDisabled={isLoading || isPurchaseSubmitting}
                hasHint
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched ? meta.error : ''}
                {...field}
              />
            )}
          </Field>
          <Field name="recipientEmail">
            {({ field, meta }) => (
              <Input
                label="Recipient's e-mail"
                isDisabled={isLoading || isPurchaseSubmitting}
                hasHint
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched ? meta.error : ''}
                {...field}
              />
            )}
          </Field>
          <Field name="buyerFullName">
            {({ field, meta }) => (
              <Input
                label="Your full name"
                isDisabled={
                  isLoading ||
                  isPurchaseSubmitting ||
                  (isClientSignedIn && field.value)
                }
                hasHint
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched ? meta.error : ''}
                {...field}
              />
            )}
          </Field>
          <Field name="recipientMessage">
            {({ field, meta }) => (
              <Textarea
                label="Message to the recipient"
                rows="4"
                isDisabled={isLoading || isPurchaseSubmitting}
                hasHint
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched ? meta.error : ''}
                maxLength="500"
                {...field}
              />
            )}
          </Field>
        </Box>
      )}
      <Field
        name="amount"
        validate={triggerValidChange({
          field: 'amount',
          schema: couponCheckoutValidationSchema,
          onChange: onChangeAmount,
          currentValue: values.amount,
        })}
      >
        {({ field, meta }) => (
          <CardSelectGroup
            label="Gift card amount"
            isDisabled={isLoading || isPurchaseSubmitting}
            isError={meta.error && meta.error !== undefined}
            hint={meta.error ? meta.error : ''}
            {...field}
            onChange={async (newValue) => {
              await setFieldValue('amount', newValue, true)
            }}
          >
            {COUPON_VALUES.map((couponValue) => (
              <CardSelectGroup.Item value={couponValue} key={couponValue}>
                <Text color={COLOR.PRIMARY_1} fontWeight={FONT_WEIGHT.MEDIUM}>
                  ${couponValue}
                </Text>
              </CardSelectGroup.Item>
            ))}
            <CardSelectGroup.Item value={field.value} isErroable>
              {({ onBlur }) => (
                <CardSelectGroup.ItemInput
                  placeholder="Custom amount"
                  type="number"
                  name="amount"
                  id="amount"
                  aria-label="Amount input"
                  maxLength={6}
                  onBlur={onBlur}
                />
              )}
            </CardSelectGroup.Item>
          </CardSelectGroup>
        )}
      </Field>
    </div>
    <PaymentElements
      card={values.card}
      paymentOption={values.paymentOption}
      setFieldValue={setFieldValue}
      isDisabled={isPurchaseSubmitting}
      availablePaymentOptions={availablePaymentOptions}
      isLoadingOptions={isLoadingOptions}
    />
    <MediaQueryWrapper
      mobile={<H4 mt={SPACE.PX_20}>Summary</H4>}
      tablet={
        <Button
          width="fit-content"
          isDisabled={isLoading}
          isLoading={isPurchaseSubmitting}
          icon={Icon.Lock}
          onClick={handleSubmit}
        >
          Confirm and pay
        </Button>
      }
    />
  </Flex>
)

CouponCheckoutElements.propTypes = {
  isPurchaseSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onChangeAmount: PropTypes.func,
  values: PropTypes.shape({
    card: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
      PropTypes.shape({
        stripeId: PropTypes.string,
        last4: PropTypes.string,
        expiryMonth: PropTypes.string,
        expiryYear: PropTypes.string,
      }),
    ]),
  }),
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isClientSignedIn: PropTypes.bool.isRequired,
  availablePaymentOptions: PropTypes.arrayOf(PropTypes.string),
  isLoadingOptions: PropTypes.bool,
}
